import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const OTP_VERIFIED_KEY = "admin_otp_verified";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isOtpVerified: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  setOtpVerified: (verified: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOtpVerified, setIsOtpVerifiedState] = useState(() => {
    // Initialize from session storage
    return sessionStorage.getItem(OTP_VERIFIED_KEY) === "true";
  });
  const [loading, setLoading] = useState(true);

  const setOtpVerified = (verified: boolean) => {
    setIsOtpVerifiedState(verified);
    if (verified) {
      sessionStorage.setItem(OTP_VERIFIED_KEY, "true");
    } else {
      sessionStorage.removeItem(OTP_VERIFIED_KEY);
    }
  };

  const checkAdminRole = async (userId: string) => {
    try {
      // Use the has_role RPC function which is SECURITY DEFINER and bypasses RLS
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: userId,
        _role: "admin",
      });

      if (error) {
        console.error("Error checking admin role:", error);
        return false;
      }
      return data === true;
    } catch (err) {
      console.error("Error in checkAdminRole:", err);
      return false;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // IMPORTANT: keep `loading` true until the role check completes.
        // This prevents race conditions where pages briefly see `isAdmin=false`
        // and incorrectly show "Access Denied".
        if (session?.user) {
          setLoading(true);
          checkAdminRole(session.user.id)
            .then(setIsAdmin)
            .finally(() => setLoading(false));
        } else {
          setIsAdmin(false);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          setLoading(true);
          return checkAdminRole(session.user.id)
            .then(setIsAdmin)
            .finally(() => setLoading(false));
        }
      })
      .finally(() => {
        // If there is no session, make sure we end loading.
        setLoading(false);
      });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    try {
      // Clear local state first
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setOtpVerified(false); // Clear OTP verification on sign out
      
      // Then attempt to sign out from Supabase
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Sign out error:", error);
    } finally {
      // Force clear any remaining session data from local storage
      // This ensures sign out works even if the server session is already invalid
      localStorage.removeItem('sb-elaapuomwpgqopakofda-auth-token');
      sessionStorage.removeItem(OTP_VERIFIED_KEY);
    }
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?mode=reset`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: redirectUrl,
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin,
        isOtpVerified,
        loading,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
        setOtpVerified,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
